import { IApplication } from "../interfaces/application";
import { IResponse } from "../interfaces/data";
import * as Constants from "./constants";
import * as Global from "./global";
import * as UI from "./ui";
import * as Util from "./util";

// data Module
// contains classes and interfaces used to communicate with web services.

// returns a Kendo DataSource that pulls data from a web service
// assumes that all paging, filtering and sorting will occur on the server
// readURL - the URL of the web service to call
// model - describes the data being returned, contains a list of all fields and their types
// customParameters - a function to call before the web service is called.  Can be used to
//      modify the request, such as adding filter parameters.
export function createWebServiceDataSource(
    readURL: string,
    model: any,
    customParameters: ((data: any) => void) | null = null,
    onError: ((status: number) => void) | null = null): kendo.data.DataSource {
    const app: IApplication = Global.app();
    const dataSource: any = new kendo.data.DataSource({
        transport: {
            read: {
                url: app.webServiceBaseURL + readURL,
                contentType: "application/json; charset=utf-8",
                dataType: "json",
                type: "POST",
                beforeSend(req: any): void {
                    if (app.userSession != null && app.authenticationToken != null) {
                        // used has logged in, so pass the token in the header of every request
                        req.setRequestHeader("Authorization", "token " + app.authenticationToken);
                    }
                },
            },
            parameterMap(data: any, operation: any): string {
                if (customParameters !== null) {
                    // the customParameters function should add any data it
                    // needs to the data object.
                    customParameters(data);
                }
                return JSON.stringify(data);
            },
        },
        serverPaging: true,
        serverSorting: true,
        serverFiltering: true,
        pageSize: 100,
        schema:
        {
            data: "Payload.Data",
            total: "Payload.Total",
            model,
        },
        error(e: any): void {
            let status: number = 0;

            if (e != null && e.xhr != null) {
                status = e.xhr.status;
                Util.handleAjaxError(e.xhr);
            } else {
                UI.showErrorMessage([Constants.PHRASE_GENERALACTIONFAILURE]);
            }

            if (onError !== null) {
                onError(status);
            }
        },
        requestEnd(e: any): void {
            let response: IResponse<any>;

            if (e != null && e.response != null) {
                // call completed and we got response back from the server
                response = e.response;
                if (response.Success) {
                    if (response.RotateAuthenticationToken) {
                        // need to start using a new token
                        Global.app().setAuthenticationToken(response.AuthenticationToken);
                    }
                } else {
                    // display errors returned from web service
                    UI.showErrorMessage(response.ErrorMessages);
                }
            }
        },
    });

    return dataSource;
}

// returns a Kendo DataSource that pulls data from a web service
// for use by an Autocomplete control
// assumes that all filtering and sorting will occur on the server
// readURL - the URL of the web service to call
// customParameters - a function to call before the web service is called.  Can be used to
//      modify the request, such as adding filter parameters.
export function createAutocompleteDataSource(
    readURL: string,
    customParameters: ((data: any) => void) | null): kendo.data.DataSource {

    const app: IApplication = Global.app();
    const dataSource: any = new kendo.data.DataSource({
        transport: {
            read: {
                url: app.webServiceBaseURL + readURL,
                contentType: "application/json; charset=utf-8", // tells the web service to serialize JSON
                dataType: "json", // send JSON to the web service
                type: "POST", // use HTTP POST so parameters are send as a post body
                beforeSend(req: any): void {
                    if (app.userSession != null && app.authenticationToken != null) {
                        // used has logged in, so pass the token in the header of every request
                        req.setRequestHeader("Authorization", "token " + app.authenticationToken);
                    }
                },
            },
            parameterMap(data: any, operation: any): string {
                const parameters: any = {
                    SearchText: "",
                    MaxRecords: 50,
                };

                if (operation === "read") {
                    // kendo creates this big filter object.  We just want to pull
                    // out the value that the user typed and let the web service
                    // determine how to filter.
                    parameters.SearchText = data.filter.filters[0].value;
                }

                if (customParameters !== null) {
                    // the customParameters function should add any data it
                    // needs to the data object.
                    customParameters(parameters);
                }
                return JSON.stringify(parameters);
            },
        },
        serverFiltering: true,
        schema:
        {
            data: "Payload",
        },
        error(e: any): void {
            if (e != null && e.xhr != null) {
                Util.handleAjaxError(e.xhr);
            } else {
                UI.showErrorMessage([Constants.PHRASE_GENERALACTIONFAILURE]);
            }
        },
        requestEnd(e: any): void {
            if (e == null || e.response == null) {
                UI.showErrorMessage([Constants.PHRASE_GENERALACTIONFAILURE]);
            } else if (e.response.Success) {
                if (e.response.RotateAuthenticationToken) {
                    // need to start using a new token
                    Global.app().setAuthenticationToken(e.response.AuthenticationToken);
                }
            } else {
                // display errors returned from web service
                UI.showErrorMessage(e.response.Messages);
            }
        },
    });

    return dataSource;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/data.ts